import React from "react"
import { Redirect, Route, Switch, useLocation } from "react-router-dom"
import cx from "classnames"

import ScrollToTop from "ScrollToTop"
import legacyUrls from "./utils/legacyUrls"

import Home from "./containers/Home/Home"
import Activity from "./containers/Activity/Activity"
import Media from "./containers/Media/Media"
import VideoGallery from "./containers/VideoGallery/VideoGallery"
import Blog from "./containers/Blog/Blog"
import BlogPost from "./containers/Blog/BlogPost"
import Page404 from "./containers/Responses/Page404"
import HipaaTraining from "./containers/HipaaTraining/HipaaTraining"
import LocalJobs from "./containers/Jobs/LocalJobs"
import State from "./containers/Jobs/State"
import County from "./containers/Jobs/County"
import City from "./containers/Jobs/City"
import ThankYou from "./containers/ThankYou/ThankYou"

function lazyLoad(component: string) {
  return React.lazy(() => import(`./containers/${component}`))
}

const About = lazyLoad("About/About")
const EndUserAgreement = lazyLoad("Legal/EndUserAgreement")
const TermsOfService = lazyLoad("Legal/TermsOfService")
const PrivacyPolicy = lazyLoad("Legal/PrivacyPolicy")
const IndependentContractorPolicy = lazyLoad(
  "Legal/IndependentContractorPolicy"
)
const Careers = lazyLoad("Careers/Careers")
const CareersDetail = lazyLoad("Careers/CareersDetail")
const VirtualVisits = lazyLoad("Forms/VirtualVisits")
const SecurityIncidents = lazyLoad("Forms/VirtualVisits")
const ClientIncidents = lazyLoad("Forms/ClientIncidents")
const ChangeManagement = lazyLoad("Forms/ChangeManagement")
const Partnerships = lazyLoad("Partnerships/Partnerships")
const ContactSales = lazyLoad("Forms/ContactSales")
const ContactUs = lazyLoad("Forms/ContactUs")
const ContactUsSent = lazyLoad("ContactUsSent/ContactUsSent")
const Locations = lazyLoad("Locations/Locations")
const Enterprise = lazyLoad("Enterprise/Enterprise")
const Program = lazyLoad("Programs/Program")
const LHH = lazyLoad("LHH/LHH")
const Amazon = lazyLoad("Amazon")
const Anthem = lazyLoad("Anthem")
const BravenHealth = lazyLoad("BravenHealth")
const BlueCross = lazyLoad("BlueCross")
const BlueCrossBlueShieldRhodeIsland = lazyLoad(
  "BlueCrossBlueShieldRhodeIsland"
)
const Kaiser = lazyLoad("Kaiser")
const VisitRating = lazyLoad("VisitRating/VisitRating")
const FormSent = lazyLoad("FormSent/FormSent")
const PressPost = lazyLoad("Press/PressPost")
const PalApp = lazyLoad("PalApp")
const PalOnboarding = lazyLoad("PalOnboarding")

const Routes: React.FC = () => {
  const { pathname } = useLocation()

  const classes = cx({
    "app-padding": !pathname.includes("/pals/signup"),
  })

  return (
    <div id="app__content" className={classes}>
      <ScrollToTop>
        <Switch>
          <Route exact path="/" component={Home} />
          <Route
            exact
            path={[
              "/pet-help-for-seniors",
              "/pricing",
              "/grocery-shopping-services-for-seniors",
              "/demo-media",
            ]}
            component={Home}
          />
          <Route exact path="/enterprise" component={Enterprise} />
          <Route exact path="/activities/:slug" component={Activity} />
          <Route
            exact
            path={[
              "/papa-pal-qualified-application",
              "/papa-pal-qualified-application-:utmSource",
              "/papa-pal-qualified-application?utm_source=:utmSource",
              "/papa-pal-qualified-application?utm_source=:utmSource&utm_campaign=:utmCampaign",
            ]}
            component={PalOnboarding}
          />
          <Route
            exact
            path={["/registration-form-page"]}
            component={PalOnboarding}
          />
          <Route
            exact
            path="/health-plans"
            component={() => <Program name="health-plans" />}
          />
          <Route
            exact
            path="/employer-benefits"
            component={() => <Program name="employer-benefits" />}
          />
          <Route exact path="/locations" component={Locations} />
          <Route exact path="/cities" component={Locations} />

          <Route exact path="/blog" component={Blog} />
          <Route
            exact
            path={["/author", "/category", "/tag"]}
            component={Blog}
          />

          <Route exact path="/blog/:slug" component={BlogPost} />
          <Route exact path={legacyUrls} component={Blog} />

          <Route exact path="/careers" component={Careers} />
          <Route exact path="/careers/:id/:slug" component={CareersDetail} />

          <Route exact path="/media" component={Media} />
          <Route exact path="/press-releases/:slug" component={PressPost} />
          <Route exact path="/video-gallery" component={VideoGallery} />
          <Route exact path="/about-papa-team" component={About} />
          <Route exact path="/contact-sales" component={ContactSales} />
          <Route exact path="/contact-us" component={ContactUs} />
          <Route exact path="/contact-us-sent" component={ContactUsSent} />
          <Redirect from="/reviews" to="/" />
          <Route exact path="/change-management" component={ChangeManagement} />
          <Route exact path="/report-incident" component={SecurityIncidents} />
          <Route
            exact
            path="/report-incident-sent"
            component={() => FormSent("security")}
          />
          <Route exact path="/member-incidents" component={ClientIncidents} />
          <Route
            exact
            path="/member-incidents-sent"
            component={() => FormSent("member")}
          />

          <Route exact path="/terms-of-use" component={TermsOfService} />
          <Route exact path="/privacy-policy" component={PrivacyPolicy} />
          <Route
            exact
            path="/independent-contractor-policy"
            component={IndependentContractorPolicy}
          />
          <Route
            exact
            path="/end-user-agreement"
            component={EndUserAgreement}
          />
          <Route
            exact
            path={["/store", "/shop"]}
            component={() => {
              window.location.href = "https://store.joinpapa.com"
              return null
            }}
          />
          <Route
            exact
            path="/virtual-companionship-visits"
            component={VirtualVisits}
          />
          <Route
            exact
            path={["/rate-your-visit/:id", "/califica-tu-visita/:id"]}
            component={VisitRating}
          />
          <Redirect
            from="/activities/coronavirus-grocery-deliveries"
            to="/activities/grocery-deliveries"
          />
          <Route exact path="/hipaa-privacy-rule" component={HipaaTraining} />
          <Route exact path="/lotsahelpinghands" component={LHH} />
          <Route exact path="/p/amazon" component={Amazon} />
          <Route exact path="/p/anthem" component={Anthem} />
          <Route exact path="/p/template" component={BlueCross} />
          <Route exact path="/p/kaiser" component={Kaiser} />
          <Route exact path="/product/pal-app" component={PalApp} />

          <Route exact path="/regence" component={Partnerships} />
          <Route exact path="/p/bravenhealth" component={BravenHealth} />
          <Redirect exact from="/bravenhealth" to="/p/bravenhealth" />
          <Route
            exact
            path="/p/bcbsri"
            component={BlueCrossBlueShieldRhodeIsland}
          />
          <Redirect exact from="/bcbsri" to="/p/bcbsri" />
          <Route exact path="/asuris" component={Partnerships} />

          <Route
            exact
            path={["/pal-local-jobs", "/pal-local-jobs&utm_source=:utm"]}
            component={LocalJobs}
          />
          <Route exact path="/pal-local-jobs/:state" component={State} />
          <Route
            exact
            path="/pal-local-jobs/:state/:county"
            component={County}
          />
          <Route
            exact
            path="/pal-local-jobs/:state/:county/:city"
            component={City}
          />

          <Route
            exact
            path="/palsupport"
            component={() => {
              window.location.href =
                "https://joinpapa.atlassian.net/servicedesk/customer/portals"
              return null
            }}
          />

          <Route exact path="/pal-application-completed" component={ThankYou} />

          <Route
            exact
            path={[
              "/pals/join",
              "/pals/signup",
              "/pals/signup?phone=:phone",
              "/pals/signup?code=:code&phone=:phone",
            ]}
            component={PalOnboarding}
          />

          <Route
            exact
            path="/help"
            component={() => {
              window.location.href =
                "https://joinpapa.notion.site/Welcome-to-the-Pal-Content-Hub-08b4397cecb74fbc8323b51bade4d53f"
              return null
            }}
          />

          <Route component={Page404} />
        </Switch>
      </ScrollToTop>
    </div>
  )
}

export default Routes
